import { SettlementPaymentRecipientDto } from '@/api/generated';

export type SettlementsTranslation = {
  balance: {
    invoiced: string;
    invoicedHint: string;
    paid: string;
    paidHint: string;
    quoted: string;
    quotedHint: string;
    remainingToPay: string;
    remainingToPayHint: string;
  };
  deleteDialog: {
    cancel: string;
    confirm: string;
    content: string;
    linkedPayments: string;
    paymentTitle: string;
    quoteTitle: string;
  };
  description: string;
  files: {
    addedAt: string;
    addedBy: string;
    close: string;
    description: string;
    empty: string;
    name: string;
    open: string;
    size: string;
    title: string;
  };
  form: {
    amount: string;
    amountFormat: string;
    amountMax: string;
    amountPositive: string;
    cancel: string;
    invoiceNumber: string;
    noQuote: string;
    paid: string;
    paymentDescription: string;
    quote: string;
    quoteDescription: string;
    recipient: string;
    save: string;
  };
  loadError: string;
  noAccess: string;
  paymentDialog: {
    createTitle: string;
    editTitle: string;
  };
  payments: {
    add: string;
    amount: string;
    createdAt: string;
    delete: string;
    description: string;
    edit: string;
    empty: string;
    fromQuote: string;
    invoiceNumber: string;
    markPaid: string;
    markUnpaid: string;
    noQuote: string;
    paid: string;
    recipient: string;
    title: string;
  };
  quoteDialog: {
    createTitle: string;
    editTitle: string;
  };
  quotes: {
    add: string;
    amount: string;
    createdAt: string;
    delete: string;
    description: string;
    edit: string;
    empty: string;
    invoiced: string;
    invoices: string;
    remainingToPay: string;
    title: string;
  };
  recipients: Record<SettlementPaymentRecipientDto, string>;
  title: string;
  toDo: {
    details: string;
    empty: string;
    invoice: string;
    markPaid: string;
    title: string;
    total: string;
    unpaidInvoices: string;
  };
};

export const settlementsTranslation: SettlementsTranslation = {
  balance: {
    invoiced: 'W fakturach',
    invoicedHint: 'Suma wszystkich płatności',
    paid: 'Zapłacone',
    paidHint: 'Płatności oznaczone jako zapłacone',
    quoted: 'W wycenie',
    quotedHint: 'Suma wszystkich wycen',
    remainingToPay: 'Pozostało do zapłaty',
    remainingToPayHint: 'Wyceny minus kwoty z faktur',
  },
  deleteDialog: {
    cancel: 'Anuluj',
    confirm: 'Usuń',
    content: 'Wpis zniknie z listy i z bilansu. Tego nie da się cofnąć.',
    linkedPayments: 'Przypisane do niej płatności ({{count}}) zostaną bez wyceny.',
    paymentTitle: 'Usunąć płatność?',
    quoteTitle: 'Usunąć wycenę?',
  },
  description: 'Wyceny prac i płatności za nie. Bilans liczy się sam z wpisów poniżej.',
  files: {
    addedAt: 'Dodano',
    addedBy: 'Kto dodał',
    close: 'Zamknij',
    description:
      'Przeciągnij plik albo kliknij, żeby wybrać. Skan lub PDF faktury zostaje przy tej płatności.',
    empty: 'Nie ma jeszcze żadnego pliku.',
    name: 'Nazwa',
    open: 'Pliki faktury',
    size: 'Rozmiar',
    title: 'Pliki faktury {{invoiceNumber}}',
  },
  form: {
    amount: 'Kwota',
    amountFormat: 'Podaj kwotę, np. 1500 albo 1500,50',
    amountMax: 'Kwota nie może przekraczać 9 999 999 999,99 zł',
    amountPositive: 'Kwota musi być większa od zera',
    cancel: 'Anuluj',
    invoiceNumber: 'Nr faktury',
    noQuote: 'Bez wyceny',
    paid: 'Zapłacona',
    paymentDescription: 'Za co',
    quote: 'Z wyceny',
    quoteDescription: 'Co',
    recipient: 'Dla kogo',
    save: 'Zapisz',
  },
  loadError: 'Nie udało się wczytać rozliczeń.',
  noAccess: 'Brak dostępu do rozliczeń.',
  paymentDialog: {
    createTitle: 'Nowa płatność',
    editTitle: 'Edycja płatności',
  },
  payments: {
    add: 'Dodaj płatność',
    amount: 'Kwota',
    createdAt: 'Dodano',
    delete: 'Usuń płatność',
    description: 'Za co',
    edit: 'Edytuj płatność',
    empty: 'Nie ma jeszcze żadnej płatności.',
    fromQuote: 'z wyceny: {{quote}}',
    invoiceNumber: 'Nr faktury',
    markPaid: 'Oznacz jako zapłaconą',
    markUnpaid: 'Oznacz jako niezapłaconą',
    noQuote: 'bez wyceny',
    paid: 'Zapłacona',
    recipient: 'Dla kogo',
    title: 'Płatności',
  },
  quoteDialog: {
    createTitle: 'Nowa wycena',
    editTitle: 'Edycja wyceny',
  },
  quotes: {
    add: 'Dodaj wycenę',
    amount: 'Kwota',
    createdAt: 'Dodano',
    delete: 'Usuń wycenę',
    description: 'Co',
    edit: 'Edytuj wycenę',
    empty: 'Nie ma jeszcze żadnej wyceny.',
    invoiced: 'W fakturach',
    invoices: 'Faktury: {{numbers}}',
    remainingToPay: 'Pozostało',
    title: 'Wyceny',
  },
  recipients: {
    [SettlementPaymentRecipientDto.Igor]: 'Igor',
    [SettlementPaymentRecipientDto.Robert]: 'Robert',
  },
  title: 'Rozliczenia',
  toDo: {
    details: 'Dodano {{date}} · dla: {{recipient}}',
    empty: 'Nic do zrobienia — wszystkie faktury są opłacone.',
    invoice: 'Faktura {{invoiceNumber}}',
    markPaid: 'Oznacz jako zapłaconą',
    title: 'Do zrobienia',
    total: 'Łącznie: {{amount}}',
    unpaidInvoices: 'Faktury do zapłaty: {{invoices}}',
  },
};
